import {AxiosProgressEvent, CancelToken} from 'axios';
import {Nodes, ServerNode} from 'common/src/modules/nodes/nodes.types';
import {NodePosition} from 'common/src/components/CLCardsMap/Canvas/Canvas.types';
import {RemoteNodeIssue} from 'common/src/components/CLCardsMap/CardsMapContainer/CardsMapContainer.types';
import apiV2Service from 'common/src/modules/api/api-v2.service';
import apiService from 'common/src/modules/api/api.service';
import {createPathParamsUrl} from 'common/src/modules/api/createPathParamsUrl';
import {
  CampaignBrief,
  CampaignBriefResponse,
  CampaignMiniBrief,
  CampaignMiniBriefResponse,
  RemoteCampaignMiniBriefResponse,
} from 'common/src/modules/campaigns/campaignBrief/campaignBrief.types';
import {
  toClientMacroItems,
  toServerCampaign,
} from 'common/src/modules/campaigns/campaigns.dataConverters';
import {
  DTCampaignVersionType,
  DuplicateNodeResponse,
  FCCampaign,
} from 'common/src/modules/campaigns/campaigns.types';
import {toLocalConditionCategories} from 'common/src/modules/conditions/conditions.dataConverter';
import {
  ConditionCategory,
  ConditionWizardCategory,
  ServerConditionWizardCategory,
} from 'common/src/modules/conditions/conditions.types';
import {
  ScheduleTiming,
  ServerCampaignVersion,
  ServerCampaignWithDT,
  ServerDecisionTree,
} from 'common/src/modules/decisionTree/decisionTree.types';
import enumsService from 'common/src/modules/enums/enums.service';
import {CLEnumType} from 'common/src/modules/enums/enums.types';
import {fileUploadApi} from 'common/src/modules/fileUpload/fileUpload.api';
import {JobExecutionParams} from 'common/src/modules/jobs/jobs.types';
import waitForJob from 'common/src/modules/jobs/utils/waitForJob';
import {MacroItem} from 'common/src/modules/macros/macros.types';
import taxonomyApi from 'common/src/modules/taxonomy/taxonomy.api';
import addRandomKeyToObjectArray from 'common/src/utils/arrayUtils/addRandomKeyToObjectArray';
import parseFileName from 'common/src/utils/fileUtils/parseFileName';
import _map from 'lodash/map';
import {
  dtToClientModel,
  dtVersionToClientModel,
  toClientCampaignMiniBrief,
  toServerCampaignMiniBrief,
} from './campaignVersions.dataConverters';
import {createDBNode} from 'common/src/modules/campaignVersions/utils/createDBNode';

const v1BasePath = 'campaigns';
const v2BasePath = 'Campaign';

async function getConditionCategories(campaignId: number): Promise<ConditionWizardCategory[]> {
  const {data: conditionCategories} = await apiV2Service.executeGetRequest<
    ServerConditionWizardCategory[]
  >(`${v2BasePath}/GetConditionCategories`, {campaignId});

  return toLocalConditionCategories(conditionCategories);
}

async function getConditionOptions({
  campaignId,
  versionId,
  categories,
}: {
  campaignId: number;
  versionId: number;
  categories: ConditionCategory[];
}): Promise<MacroItem[]> {
  const url = createPathParamsUrl(`${v2BasePath}/:campaignId/version/:versionId/conditions`, {
    campaignId,
    versionId,
  });

  const [conditions, taxonomy] = await Promise.all([
    apiV2Service.executePostRequest<MacroItem[]>(url, {}, {categories}).then((res) => res.data),
    taxonomyApi.getMapping(),
  ]);

  return toClientMacroItems(conditions, taxonomy, categories[0]);
}

async function getDTCampaign(campaignId: number, versionNumber: string | DTCampaignVersionType) {
  const baseUrl = `${v2BasePath}/:campaignId/version`;
  let url: string;

  switch (versionNumber) {
    case DTCampaignVersionType.Latest:
      url = createPathParamsUrl(`${baseUrl}/latest`, {
        campaignId,
      });
      break;
    case DTCampaignVersionType.Draft:
      url = createPathParamsUrl(`${baseUrl}/draft`, {
        campaignId,
      });
      break;
    default:
      url = createPathParamsUrl(`${baseUrl}/number/:versionNumber`, {
        campaignId,
        versionNumber,
      });
      break;
  }

  const [generalEnums, campaignEnums, relatedEnums, {data}] = await Promise.all([
    enumsService.loadEnums(CLEnumType.General),
    enumsService.loadEnums(CLEnumType.CampaignPage),
    enumsService.loadEnums(CLEnumType.RelatedItems),
    apiV2Service.executeGetRequest<ServerCampaignWithDT>(url, {}),
  ]);

  return dtToClientModel({
    decisionTree: data.loadedVersion.decisionTree,
    version: data.loadedVersion,
    campaignEnums,
    generalEnums,
    relatedEnums,
  });
}

function updateVersionMetadata({
  campaignId,
  versionId,
  name,
  description,
}: {
  campaignId: number;
  versionId: number;
  description: string;
  name: string;
}) {
  const url = createPathParamsUrl(`${v2BasePath}/:campaignId/version/:versionId/metadata`, {
    campaignId,
    versionId,
  });

  return apiV2Service.executePutRequest(url, {}, {name, description}, {sendAsFormData: false});
}

function updateMultipleNodePositions(
  campaignId: number,
  versionNumber: number,
  nodePositions: NodePosition[],
) {
  const url = createPathParamsUrl(
    `${v1BasePath}/:campaignId/versions/:versionNumber/nodePositions`,
    {
      campaignId,
      versionNumber,
    },
  );

  return apiService.executePutRequest<[]>(
    url,
    {},
    {nodeCoordinates: [...nodePositions]},
    {sendAsFormData: false},
  );
}

async function activateVersion(
  campaignId: number,
  versionNumber: number,
  activateOnReject: boolean,
) {
  const url = createPathParamsUrl(`${v1BasePath}/:campaignId/versions/:versionNumber/activate`, {
    campaignId,
    versionNumber,
  });

  return apiService
    .executePostRequest<JobExecutionParams>(url, {}, {activateOnReject}, {sendAsFormData: false})
    .then(({data: {executionId: jobId}}) =>
      jobId > 0 ? waitForJob({jobId, jobName: 'Activate Campaign Version'}) : null,
    );
}

async function scheduleVersionActivation(
  campaignId: number,
  versionNumber: number,
  cronExpression: string,
  timeZone: string,
  activateOnReject: boolean,
) {
  return apiV2Service.executePostRequest(
    `${v2BasePath}/${campaignId}/version/number/${versionNumber}/scheduledActivation`,
    {},
    {
      cronExpression,
      timeZone,
      ...(activateOnReject !== undefined ? {activation: {activateOnReject}} : {}),
    },
  );
}

async function cancelVersionScheduledActivation(campaignId: number, versionNumber: number) {
  return apiV2Service.executeDeleteRequest(
    `${v2BasePath}/${campaignId}/version/number/${versionNumber}/scheduledActivation`,
  );
}

async function getCampaignRenderingSchedule(campaignId: number) {
  const url = createPathParamsUrl(
    `${v2BasePath}/${campaignId}/ActiveVersion/AdServingAssets/UpdateSchedule`,
    {
      campaignId,
    },
  );

  const {data} = await apiV2Service.executeGetRequest<ScheduleTiming>(url);

  return data;
}

async function setCampaignRenderingSchedule(
  campaignId: number,
  cronExpression: string,
  timeZone: string,
) {
  const url = createPathParamsUrl(
    `${v2BasePath}/${campaignId}/ActiveVersion/AdServingAssets/UpdateSchedule`,
    {
      campaignId,
    },
  );

  return apiV2Service.executePostRequest(
    url,
    {},
    {cronExpression, timeZone},
    {sendAsFormData: false},
  );
}

async function disableCampaignRenderingSchedule(campaignId: number) {
  const url = createPathParamsUrl(
    `${v2BasePath}/${campaignId}/ActiveVersion/AdServingAssets/UpdateSchedule`,
    {
      campaignId,
    },
  );

  return apiV2Service.executeDeleteRequest(url, {});
}

async function saveCampaignVersion({
  description,
  name,
  campaignId,
  versionId,
}: {
  campaignId: number;
  name: string;
  versionId: number;
  description: string;
}) {
  const url = createPathParamsUrl(`${v2BasePath}/:campaignId/version/:versionId/save`, {
    campaignId,
    versionId,
  });

  return apiV2Service.executePostRequest<ServerCampaignWithDT>(
    url,
    {},
    {name, description},
    {sendAsFormData: false},
  );
}

async function getCampaignVersionsList(campaignId: number) {
  const url = createPathParamsUrl(`${v2BasePath}/:campaignId/GetVersionsMetadata`, {
    campaignId,
  });

  const {data: versions} = await apiV2Service.executeGetRequest<ServerCampaignVersion[]>(url, {});

  return _map(versions, dtVersionToClientModel);
}

async function deleteCampaignVersion({
  campaignId,
  versionNumber,
}: {
  campaignId: number;
  versionNumber: number;
}) {
  const url = createPathParamsUrl(`${v1BasePath}/:campaignId/versions/:versionNumber`, {
    campaignId,
    versionNumber,
  });

  return apiService.executeDeleteRequest(url, {});
}

async function createDraftFromVersion({
  campaignId,
  versionNumber,
  versionName,
}: {
  campaignId: number;
  versionNumber: number;
  versionName: string;
}) {
  const url = createPathParamsUrl(
    `${v1BasePath}/:campaignId/versions/:versionNumber/restoreToDraft`,
    {
      campaignId,
      versionNumber,
    },
  );

  return apiService.executePostRequest<[]>(
    url,
    {},
    {description: `Created from version '${versionName}'`},
    {sendAsFormData: false},
  );
}

async function getCampaignIssues(campaignId: number, versionNumber: number) {
  const url = createPathParamsUrl(`${v1BasePath}/:campaignId/versions/:versionNumber/issues`, {
    campaignId,
    versionNumber,
  });

  const {
    data: [issues],
  } = await apiService.executeGetRequest<RemoteNodeIssue[][]>(url, {});

  return addRandomKeyToObjectArray(issues).map((issue) => {
    return {...issue, issueKey: issue.key};
  });
}

export interface PatchTreeParams {
  campaignId: number;
  versionNumber: number;
  nodesToDelete?: Nodes;
  nodesToUpdate?: Nodes;
  nodesToAdd?: Nodes;
  campaignNode?: FCCampaign;
  rootNodeId?: number;
  fallbackCreativeNodeId?: number;
}

async function updateTree({
  campaignId,
  versionNumber,
  nodesToAdd,
  nodesToUpdate,
  nodesToDelete,
  campaignNode,
  rootNodeId,
  fallbackCreativeNodeId,
}: PatchTreeParams) {
  const url = createPathParamsUrl(`${v2BasePath}/:campaignId/version/number/:versionNumber/tree`, {
    campaignId,
    versionNumber,
  });

  const serverNodesToAdd = _map(nodesToAdd, createDBNode);
  const serverNodesToUpdate = _map(nodesToUpdate, createDBNode);
  const serverNodesToDelete = _map(nodesToDelete, createDBNode);

  const formObj = {
    deletedNodes: serverNodesToDelete,
    newNodes: serverNodesToAdd,
    updatedNodes: serverNodesToUpdate,
    ...(campaignNode && {campaignNode: {campaign: toServerCampaign(campaignNode)}}),
    rootNodeId,
    fallbackCreativeNodeId,
  };

  const [generalEnums, campaignEnums, relatedEnums, {data}] = await Promise.all([
    enumsService.loadEnums(CLEnumType.General),
    enumsService.loadEnums(CLEnumType.CampaignPage),
    enumsService.loadEnums(CLEnumType.RelatedItems),
    apiV2Service.executePatchRequest<ServerCampaignWithDT>(url, {}, formObj, {
      sendAsFormData: false,
    }),
  ]);

  return dtToClientModel({
    decisionTree: data.loadedVersion.decisionTree,
    version: data.loadedVersion,
    campaignEnums,
    generalEnums,
    relatedEnums,
  });
}

async function isRendering(campaignId: number) {
  const url = createPathParamsUrl(`${v2BasePath}/:campaignId/IsRendering`, {
    campaignId,
  });

  const {data} = await apiV2Service.executeGetRequest<boolean>(url);

  return data;
}

function getBrief(campaignId: number, versionId: number): Promise<CampaignBriefResponse> {
  return apiV2Service
    .executeGetRequest<CampaignBriefResponse>(
      `${v2BasePath}/${campaignId}/version/${versionId}/brief`,
    )
    .then((res) => res.data);
}

function updateBrief(campaignId: number, versionId: number, brief: CampaignBrief) {
  return apiV2Service.executePutRequest<void>(
    `${v2BasePath}/${campaignId}/version/${versionId}/brief`,
    {},
    {brief},
  );
}

async function getConcept(campaignId: number, versionId: number) {
  const url = `${v2BasePath}/${campaignId}/version/${versionId}/conceptplan`;

  const [generalEnums, campaignEnums, relatedEnums, {data}] = await Promise.all([
    enumsService.loadEnums(CLEnumType.General),
    enumsService.loadEnums(CLEnumType.CampaignPage),
    enumsService.loadEnums(CLEnumType.RelatedItems),
    apiV2Service.executeGetRequest<ServerDecisionTree>(url, {}),
  ]);

  return dtToClientModel({
    decisionTree: data,
    campaignEnums,
    generalEnums,
    relatedEnums,
  });
}

async function updateConcept({
  campaignId,
  versionId,
  nodesToAdd,
  nodesToUpdate,
  nodesToDelete,
  campaignNode,
  rootNodeId,
  fallbackCreativeNodeId,
}: {
  campaignId: number;
  versionId: number;
  nodesToDelete?: Nodes;
  nodesToUpdate?: Nodes;
  nodesToAdd?: Nodes;
  campaignNode?: FCCampaign;
  rootNodeId?: number;
  fallbackCreativeNodeId?: number;
}) {
  const url = `${v2BasePath}/${campaignId}/version/${versionId}/patchConceptPlan`;

  const serverNodesToAdd = _map(nodesToAdd, createDBNode);
  const serverNodesToUpdate = _map(nodesToUpdate, createDBNode);
  const serverNodesToDelete = _map(nodesToDelete, createDBNode);

  const formObj = {
    deletedNodes: serverNodesToDelete,
    newNodes: serverNodesToAdd,
    updatedNodes: serverNodesToUpdate,
    ...(campaignNode && {campaignNode: {campaign: toServerCampaign(campaignNode)}}),
    rootNodeId,
    fallbackCreativeNodeId,
  };

  const [generalEnums, campaignEnums, relatedEnums, {data}] = await Promise.all([
    enumsService.loadEnums(CLEnumType.General),
    enumsService.loadEnums(CLEnumType.CampaignPage),
    enumsService.loadEnums(CLEnumType.RelatedItems),
    apiV2Service.executePatchRequest<ServerDecisionTree>(url, {}, formObj, {
      sendAsFormData: false,
    }),
  ]);

  return dtToClientModel({
    decisionTree: data,
    campaignEnums,
    generalEnums,
    relatedEnums,
  });
}

function updateConceptNodePositions(
  campaignId: number,
  versionId: number,
  nodePositions: NodePosition[],
) {
  return apiService.executePutRequest<[]>(
    `${v1BasePath}/${campaignId}/versions/id/${versionId}/conceptNodePositions`,
    {},
    {nodeCoordinates: [...nodePositions]},
    {sendAsFormData: false},
  );
}

function uploadCreativePoster(
  file: File,
  campaignId: number,
  nodeId: number,
  cancelToken: CancelToken,
  onUploadProgress: (progressEvent: AxiosProgressEvent) => void,
): Promise<string> {
  return fileUploadApi
    .getPresignedUrlForCampaignImageFileUpload({
      campaignId,
      nodeId,
      fileExtension: parseFileName(file.name).fileExtension || '',
      contentType: file.type,
    })
    .then(({presignedUrl, finalCdnUrl}) =>
      fileUploadApi
        .uploadFileToS3({
          file,
          cancelToken,
          url: presignedUrl,
          onUploadProgress,
          contentType: file.type,
        })
        .then(() => finalCdnUrl),
    );
}

async function importFromConceptPlan({
  campaignId,
  versionId,
}: {
  campaignId: number;
  versionId: number;
}) {
  const [generalEnums, campaignEnums, relatedEnums, {data}] = await Promise.all([
    enumsService.loadEnums(CLEnumType.General),
    enumsService.loadEnums(CLEnumType.CampaignPage),
    enumsService.loadEnums(CLEnumType.RelatedItems),
    apiV2Service.executePostRequest<ServerDecisionTree>(
      `${v2BasePath}/${campaignId}/version/${versionId}/GenerateDraftFromConceptPlan`,
      {},
      {},
      {
        sendAsFormData: false,
      },
    ),
  ]);

  return dtToClientModel({
    decisionTree: data,
    campaignEnums,
    generalEnums,
    relatedEnums,
  });
}

async function validateNodesBeforeDuplication({
  fromCampaignId,
  toCampaignId,
  nodes,
}: {
  fromCampaignId: number;
  toCampaignId: number;
  nodes: ServerNode[];
}): Promise<{invalidNodes: DuplicateNodeResponse[]}> {
  return apiV2Service
    .executePostRequest<{
      invalidNodes: DuplicateNodeResponse[];
    }>(
      `${v2BasePath}/ValidateNodesBeforeDuplication/from/${fromCampaignId}/to/${toCampaignId}`,
      {},
      {
        nodes,
      },
    )
    .then((res) => res.data);
}

function getMiniBrief(campaignId: number, versionId: number): Promise<CampaignMiniBriefResponse> {
  return apiV2Service
    .executeGetRequest<RemoteCampaignMiniBriefResponse>(
      `${v2BasePath}/${campaignId}/version/${versionId}/miniBrief`,
    )
    .then(({data}) => ({
      ...data,
      brief: toClientCampaignMiniBrief(data.brief),
    }));
}

function updateMiniBrief({
  campaignId,
  versionNumber,
  brief,
}: {
  campaignId: number;
  versionNumber: number;
  brief: CampaignMiniBrief;
}) {
  return apiV2Service.executePutRequest<void>(
    `${v2BasePath}/${campaignId}/version/${versionNumber}/miniBrief`,
    {},
    {
      ...toServerCampaignMiniBrief(brief),
    },
  );
}

const campaignVersionsApi = {
  getDTCampaign,
  updateTree,
  updateMultipleNodePositions,
  saveCampaignVersion,
  getCampaignVersionsList,
  createDraftFromVersion,
  updateVersionMetadata,
  activateVersion,
  getCampaignIssues,
  deleteCampaignVersion,
  isRendering,
  getCampaignRenderingSchedule,
  setCampaignRenderingSchedule,
  disableCampaignRenderingSchedule,
  getConditionCategories,
  getConditionOptions,
  scheduleVersionActivation,
  cancelVersionScheduledActivation,
  getBrief,
  updateBrief,
  getConcept,
  updateConcept,
  updateConceptNodePositions,
  uploadCreativePoster,
  importFromConceptPlan,
  validateNodesBeforeDuplication,
  getMiniBrief,
  updateMiniBrief,
};

export default campaignVersionsApi;
